import React from "react";
import { Accordion, AttachmentList } from "@outpave/ds";
import { toAttachmentItems } from "utils/attachments/attachmentItems";
import {
  useAttachmentListLabels,
  useAttachmentViewerLabels,
} from "hooks/useAttachmentLabels";
import { Typography } from "@mui/material";
import dayjs from "utils/dayjs";
import { useTranslation } from "react-i18next";
import { titleCase } from "title-case";
import CustomFieldParentType from "enums/CustomFieldParentType";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import { formatAddressDisplay } from "hooks/useAddressData";
import { formatCashFull } from "utils/formatCash";
import {
  CashExpense,
  FormikCreateExpense,
  MileageExpense,
} from "utils/reimbursements/reimbursementExpenseFormat";
import StaticMapsImage from "components/admin/common/StaticMapsImage";
import { Stack } from "components/layout/reusableLayouts";
import Summary, { SummaryItem } from "components/common/Summary";
import { fullName } from "utils/fullName";

interface ReimbursementSummaryProps {
  expenseData: FormikCreateExpense;
  /** Optional content rendered above the summary (e.g. duplicate / bank banners). */
  topSlot?: React.ReactNode;
}

/**
 * ReimbursementSummary
 * ====================
 * Read-only recap of a reimbursement before submission. The field list is
 * `components/common/Summary`; the map preview and attachments sit around it.
 */
const ReimbursementSummary: React.FC<ReimbursementSummaryProps> = ({
  expenseData,
  topSlot,
}) => {
  const { t } = useTranslation();
  const attachmentLabels = useAttachmentListLabels();
  const viewerLabels = useAttachmentViewerLabels();

  const isCashExpense = (
    expense: FormikCreateExpense,
  ): expense is CashExpense => expense.expenseType === "CASH";

  const isMileageExpense = (
    expense: FormikCreateExpense,
  ): expense is MileageExpense => expense.expenseType === "MILEAGE";

  const { filteredCustomFieldSummaryItems } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.REIMBURSEMENT,
    customFields: expenseData?.customFields,
  });

  const rows: SummaryItem[] = [
    {
      title: t("Expense Type"),
      value: titleCase((expenseData.expenseType || "").toLowerCase()),
    },
    {
      title: t("Pay To"),
      value: fullName(expenseData.payTo) || expenseData.payTo?.email,
    },
    {
      title: t("Amount"),
      value: formatCashFull(expenseData.amount, "usd"),
    },
    {
      title: t("Date"),
      value: dayjs(expenseData.date).format("MMMM D, YYYY"),
    },
    ...(isCashExpense(expenseData)
      ? [
          {
            title: t("Merchant"),
            value: expenseData.merchant?.name,
          },
        ]
      : []),
    ...(isMileageExpense(expenseData)
      ? [
          {
            title: t("From"),
            fullWidth: true,
            value: formatAddressDisplay(expenseData.tripFrom),
          },
          {
            title: t("To"),
            fullWidth: true,
            value: formatAddressDisplay(expenseData.tripTo),
          },
          {
            title: t("Round Trip"),
            value: expenseData.isRoundTrip ? t("Yes") : t("No"),
          },
          {
            title: t("Distance"),
            value: `${expenseData.distance} ${t("miles")}`,
          },
          {
            title: t("Rate"),
            value: expenseData.rate
              ? `${formatCashFull(expenseData.rate, "usd")} ${t("per mile")}`
              : null,
          },
        ]
      : []),
    {
      title: t("Allocation"),
      fullWidth: true,
      value: expenseData?.allocationCode?.allocationCode
        ? `${expenseData.allocationCode.allocationCode} (${expenseData.allocationType?.name})`
        : null,
    },
    {
      title: t("Description"),
      fullWidth: true,
      value: expenseData.description,
    },
    ...filteredCustomFieldSummaryItems,
  ];

  return (
    <Stack gap="var(--space-5)">
      <Typography variant="h6" sx={{ color: "var(--color-text-primary)" }}>
        {t("Expense Summary")}
      </Typography>

      {topSlot}

      {isMileageExpense(expenseData) && (
        <StaticMapsImage
          tripFrom={expenseData.tripFrom}
          tripTo={expenseData.tripTo}
          polyline={expenseData.polyline}
          width="100%"
          height="auto"
          borderRadius="var(--radius-md)"
        />
      )}

      <Summary items={rows} />

      <Accordion title={t("Attachments")}>
        {/* Read-only: no onRemove and no onAdd, which is how the package
            expresses it rather than `disabled`. */}
        <AttachmentList
          files={toAttachmentItems(expenseData.attachments, t("Receipt"))}
          labels={attachmentLabels}
          viewer={{ labels: viewerLabels, filmstrip: true }}
        />
      </Accordion>
    </Stack>
  );
};

export default ReimbursementSummary;
